import type { RouteLocationNormalizedLoaded } from "vue-router";

declare module "vue-router" {
  interface RouteMeta {
    /**
     * When the page is rebuilt although the route record stays the same. App.vue
     * keys <router-view> with routeViewKey(): pages that set this up per id or
     * per query in setup() must not be reused for a different one.
     *   "path"  — a new instance whenever the path changes (route params)
     *   [keys]  — a new instance whenever one of these query params changes
     */
    remount?: "path" | readonly string[];
  }
}

/**
 * The query params that decide which match /local sets up. LocalGamePage reads
 * them once in setup(); changing only them (top bar "Play → Sherlock AI — Hard"
 * while already on /local) used to change the URL and nothing else. Other
 * params, and the history.replaceState() share URL the page installs itself,
 * are deliberately not part of the key, so nothing remounts mid-game.
 */
export const LOCAL_GAME_REMOUNT_QUERY = ["ai", "strength", "tournament"] as const;

/**
 * The <router-view> key for a route (see RouteMeta.remount above).
 * Undefined keeps Vue Router's default of reusing the page instance.
 */
export function routeViewKey(
  route: Pick<RouteLocationNormalizedLoaded, "path" | "query" | "meta">,
): string | undefined {
  const remount = route.meta.remount;
  if (remount === undefined) return undefined;
  if (remount === "path") return route.path;
  const parts = remount.map((key) => {
    const value = route.query[key];
    return `${key}=${Array.isArray(value) ? value.join(",") : value ?? ""}`;
  });
  return `${route.path}?${parts.join("&")}`;
}
